import { ApiError, type AuthMode, type ExtraHeaders, api } from '@/lib/api';
import { ApiProblem } from '@/lib/errors';

import type { SupportedLocale } from '@/i18n';

export type ApiClient = {
  get: <T>(path: string, signal?: AbortSignal) => Promise<T>;
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) => Promise<T>;
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) => Promise<T>;
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) => Promise<T>;
  del: <T>(path: string, signal?: AbortSignal) => Promise<T>;
  blob: (path: string, signal?: AbortSignal) => Promise<Blob>;
};

function rethrow<T>(p: Promise<T>): Promise<T> {
  return p.catch((error: unknown) => {
    if (error instanceof ApiError) throw ApiProblem.fromApiError(error);
    if (error instanceof TypeError) throw ApiProblem.network();
    throw error;
  });
}

export function createApiClient(
  mode: AuthMode,
  accountId: number | null,
  locale: SupportedLocale,
): ApiClient {
  const headers: ExtraHeaders = {
    'X-Locale': locale,
    'X-Account-Id': accountId,
  };

  return {
    get: <T>(path: string, signal?: AbortSignal) =>
      rethrow(api.get<T>(path, mode, signal, headers)),
    post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
      rethrow(api.post<T>(path, body, mode, signal, headers)),
    put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
      rethrow(api.put<T>(path, body, mode, signal, headers)),
    patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
      rethrow(api.patch<T>(path, body, mode, signal, headers)),
    del: <T>(path: string, signal?: AbortSignal) =>
      rethrow(api.del<T>(path, mode, signal, headers)),
    blob: (path: string, signal?: AbortSignal) => rethrow(api.blob(path, mode, signal, headers)),
  };
}
